import { ChevronDownIcon } from '@chakra-ui/icons';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { type CSSProperties, type ReactNode, useMemo } from 'react';

import { NestedFilterSelectContext } from './context';
import { NestedFilterSelectGroup } from './Group';
import { findGroupLabelForValue } from './helpers';
import { NestedFilterSelectItem } from './Item';
import styles from './nested-filter-select.module.css';

export type NestedFilterSelectCssVar =
  | '--nested-filter-select-disabled-bg'
  | '--nested-filter-select-disabled-border'
  | '--nested-filter-select-disabled-color';

function NestedFilterSelectRoot({
  value,
  selectedLabel,
  onChange,
  placeholder = 'Select',
  isDisabled = false,
  showGroup = true,
  portalContainer,
  cssVars,
  children,
  'data-testid': dataTestid,
}: {
  value: string | null;
  selectedLabel: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  showGroup?: boolean;
  portalContainer?: HTMLElement | null;
  cssVars?: Partial<Record<NestedFilterSelectCssVar, string>>;
  children: ReactNode;
  'data-testid'?: string;
}) {
  const ctx = useMemo(
    () => ({
      selectedValue: value,
      onSelectValue: onChange,
    }),
    [value, onChange]
  );

  const hasValue = value !== null && value !== '';
  const showLabel = hasValue && selectedLabel !== '';
  const resolvedGroupLabel = (() => {
    if (!showGroup || value === null) return null;
    const match = findGroupLabelForValue(children, value, null);
    return match === null ? null : match.group;
  })();

  const triggerContent = (() => {
    if (!showLabel) return placeholder;
    if (resolvedGroupLabel !== null && resolvedGroupLabel !== '') {
      return (
        <>
          <span className={styles.triggerGroup}>{resolvedGroupLabel}</span>
          <span className={styles.triggerSeparator} aria-hidden="true">
            /
          </span>
          {selectedLabel}
        </>
      );
    }
    return selectedLabel;
  })();

  return (
    <NestedFilterSelectContext.Provider value={ctx}>
      <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger
          asChild
          disabled={isDisabled}
          data-testid={dataTestid}
        >
          <button
            type="button"
            className={styles.trigger}
            data-disabled={isDisabled ? '' : undefined}
            style={cssVars as CSSProperties | undefined}
          >
            <span
              className={
                showLabel
                  ? styles.triggerLabel
                  : `${styles.triggerLabel} ${styles.triggerPlaceholder}`
              }
            >
              {triggerContent}
            </span>
            <span className={styles.chevron} aria-hidden="true">
              <ChevronDownIcon boxSize={4} />
            </span>
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal container={portalContainer ?? undefined}>
          <DropdownMenu.Content
            className={styles.content}
            sideOffset={4}
            align="start"
            collisionPadding={8}
            avoidCollisions
          >
            {children}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </NestedFilterSelectContext.Provider>
  );
}

export const NestedFilterSelect = Object.assign(NestedFilterSelectRoot, {
  Group: NestedFilterSelectGroup,
  Item: NestedFilterSelectItem,
});
